import {OSkinableInit} from "back/commons/basis";
import {AppFrameDeskFeat, IAppBuilder, IAppCtx, JAppDef} from "back/core/appFrame";
import {Action, ActionHackCtx, IAction} from "lib/commons/actions";
import {SimpleTagArea} from "lib/commons/areas";
import {IReg, IRegPointer, REG} from 'lib/commons/registry';
import {JSX} from "lib/commons/xml/dom";
import {IChainEnv} from "lib/wsp/chain";
import {IWspUiEnv, wspCd} from "lib/wsp/wsp";
import "back/wsp/plugins/plugins_Perms";
import {POPUP} from "back/commons/widgets/popups";
import {srcRef} from "lib/wsp/src";
import {TaskApp} from "back/wsp/apps/taskApp";
import {MyTasksRepos} from "back/wsp/views/myTasksRepos";


/**
 * Accès aux tâches d'un chain dans une appFrame.
 * A appeler au cours du processus d'init du desk (qui doit disposer de AppFrameDeskFeat).
 *
 * @param reg Registre à exploiter correspondant au chain, nécessaire en cas de front multi-universe.
 */
export function initApp(reg: IReg<IChainEnv> = REG.reg) {
	if (reg.env.universe.config.backEnd === 'fs') return;

	class WrapReg extends ActionHackCtx<Element, IReg<IChainEnv>> {
		constructor(sub: IAction<IReg<IChainEnv>>, public reg: IReg<IChainEnv>) {
			super(sub);
		}

		wrapCtx(ctx: Element): IReg<IChainEnv> {return this.reg}

		initButtonNode(buttonNode: Element, ctx: Element) {
			//Affectation du bon registre dans le contexte graphique.
			(buttonNode as IRegPointer<IChainEnv>).reg = this.reg;
			this.sub.initButtonNode(buttonNode, this.reg);
		}
	}

	function wrap(action: IAction<IReg<IChainEnv>>): IAction<Element> {
		return new WrapReg(action, reg);
	}

	const d = desk as AppFrameDeskFeat;
	d.addAppBuilder(new TaskAppBuilder(reg));

	// Création du menu 'Mes tâches'
	const tasksAction = wrap(new Action<IReg<IChainEnv>>('tasks')
		.setLabel("Mes tâches")
		//.setIcon("/@skin@/wsp/objects/tasksList.svg")
		.setSkinOver("appframeHeader_tasks")
		.requireVisiblePerm("ui.tasksPlg")
		.override("initButtonNode",
			function (buttonNode: Element, ctx: Element) {
				buttonNode.setAttribute("role", "menu");
			}
		));

	tasksAction.execute = async (ctx: Element, ev: Event) => {
		const {MyTasksRepos} = await import("back/wsp/views/myTasksRepos.js");
		const myTasks = new MyTasksRepos().initialize({reg});
		POPUP.showMenu(myTasks, ev.target as HTMLElement, ev.target as Node);
	};

	REG.reg.addToList("appframe:header:toolbar", "tasks", 1, tasksAction);
}

/** Définition pour une TaskApp. */
export interface JTaskAppDef extends JAppDef {
	task: srcRef
	w: wspCd
}


/** Construction async de la view TaskApp de consultation d'une tâche. */
export class TaskAppBuilder extends SimpleTagArea<IAppCtx<IChainEnv>, TaskApp> implements OSkinableInit, IAppBuilder<IChainEnv, IWspUiEnv> {

	skin?: string;

	skinOver?: string;

	constructor(public reg: IReg<IChainEnv>) {
		super('wsp-task-app', reg.env.resolver.resolve(":back:wsp/apps/taskApp.js"));
	}

	isAppDefMatch(data: JTaskAppDef) {return typeof data.task === 'string'}

	newElt(ctx: IAppCtx<IChainEnv> & OSkinableInit): TaskApp {
		if (!ctx.skin) ctx.skin = this.skin;
		if (!ctx.skinOver) ctx.skinOver = this.skinOver;
		ctx.reg = this.reg;
		return (super.newElt(ctx) as TaskApp).initialize(ctx);
	}
}
